import { useMutation, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import {
  IBulkWasteReceiptPatch,
  IUpsertWasteReceiptDraft,
  IWasteReceiptRow,
  IWasteReceiptSubmitResult,
} from "../models/wasteTracking/IWasteReceiptRow";
import { DefaultResponseWithData } from "../models/system";
import apiConfig from "../utils/apiConfig";

export const WASTE_TRACKING_TABLE_URL = "/wastereceipt";

export default function useWasteTracking(componentName = "useWasteTracking") {
  const { get, post, put } = useApiHelper(apiConfig(componentName));
  const queryClient = useQueryClient();

  // The page is a PagedTable over /wastereceipt/{view}, so every write refreshes those queries.
  const invalidate = () => {
    queryClient.invalidateQueries(["pagedTable", `${WASTE_TRACKING_TABLE_URL}/pending`]);
    queryClient.invalidateQueries(["pagedTable", `${WASTE_TRACKING_TABLE_URL}/submitted`]);
    queryClient.invalidateQueries(["pagedTable", `${WASTE_TRACKING_TABLE_URL}/excluded`]);
    queryClient.invalidateQueries(["waste-tracking"]);
  };

  const getReadyIds = (startDate?: string | null, endDate?: string | null) => {
    const params = new URLSearchParams();
    if (startDate) params.set("startDate", startDate);
    if (endDate) params.set("endDate", endDate);
    const query = params.toString();
    return get<DefaultResponseWithData<string[]>>(
      `${WASTE_TRACKING_TABLE_URL}/pending/ready-ids${query ? `?${query}` : ""}`
    );
  };

  const upsertDraft = useMutation(
    ({ vehicleId, draft }: { vehicleId: string; draft: IUpsertWasteReceiptDraft }) =>
      put<IUpsertWasteReceiptDraft, DefaultResponseWithData<IWasteReceiptRow>>(
        `${WASTE_TRACKING_TABLE_URL}/draft/${vehicleId}`,
        draft
      ),
    { onSuccess: invalidate }
  );

  const bulkUpdate = useMutation(
    (body: IBulkWasteReceiptPatch) =>
      post<IBulkWasteReceiptPatch, DefaultResponseWithData<number>>(`${WASTE_TRACKING_TABLE_URL}/bulk/update`, body),
    { onSuccess: invalidate }
  );

  const exclude = useMutation(
    (vehicleIds: string[]) =>
      post<{ vehicleIds: string[] }, DefaultResponseWithData<number>>(`${WASTE_TRACKING_TABLE_URL}/bulk/exclude`, { vehicleIds }),
    { onSuccess: invalidate }
  );

  const restore = useMutation(
    (vehicleIds: string[]) =>
      post<{ vehicleIds: string[] }, DefaultResponseWithData<number>>(`${WASTE_TRACKING_TABLE_URL}/bulk/restore`, { vehicleIds }),
    { onSuccess: invalidate }
  );

  // One chunk (max 25 ids) per call; the page loops over chunks so a big run can't time out.
  const submit = useMutation(
    (vehicleIds: string[]) =>
      post<{ vehicleIds: string[] }, DefaultResponseWithData<IWasteReceiptSubmitResult[]>>(
        `${WASTE_TRACKING_TABLE_URL}/submit`,
        { vehicleIds }
      ),
    { onSuccess: invalidate }
  );

  const amend = useMutation(
    (vehicleId: string) =>
      post<null, DefaultResponseWithData<IWasteReceiptSubmitResult>>(`${WASTE_TRACKING_TABLE_URL}/${vehicleId}/amend`, null),
    { onSuccess: invalidate }
  );

  return {
    getReadyIds,
    upsertDraft,
    bulkUpdate,
    exclude,
    restore,
    submit,
    amend,
    invalidate,
  };
}
